import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";

export interface ILeadingSource {
  _id?: string;
  name: string;
}

interface IStore {
  /** State */
  leadingSources: ILeadingSource[];
  leadingSource: ILeadingSource | null;
  addedLeadingSource: any | null;
  updatedLeadingSource: any | null;
  deletedLeadingSource: any | null;
  /** Callbacks */
  getLeadingSources: () => void;
  getLeadingSource: (id: string) => void;
  addLeadingSource: (leadingSource: ILeadingSource) => void;
  updateLeadingSource: (id: string, leadingSource: ILeadingSource) => void;
  deleteLeadingSource: (id: string) => void;
  clearAddedLeadingSource: () => void;
  clearUpdatedLeadingSource: () => void;
  clearDeletedLeadingSource: () => void;
}

export const useLeadingSources = create<IStore>()((set) => ({
  /** State */
  leadingSources: [],
  leadingSource: null,
  addedLeadingSource: null,
  updatedLeadingSource: null,
  deletedLeadingSource: null,
  /** Callbacks */
  getLeadingSources: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("leading-source");
      if (response?.data) {
        set({ leadingSources: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Leading Sources", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getLeadingSource: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`leading-source/${id}`);
      if (response?.data) {
        set({ leadingSource: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching LeadingSource", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addLeadingSource: async (leadingSource: any) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("leading-source", leadingSource);
      if (response?.data) {
        set({ addedLeadingSource: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on adding LeadingSource", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateLeadingSource: async (id: string, leadingSource: any) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`leading-source/${id}`, leadingSource);
      if (response?.data) {
        set({ updatedLeadingSource: response.data });
      }
    } catch (error) {
      LOG.error("Error on updating LeadingSource", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteLeadingSource: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`leading-source/${id}`);
      if (response?.data) {
        set((state) => ({
          deletedLeadingSource: response.data,
          leadingSources: state.leadingSources.filter(
            (leadingSource) => leadingSource._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting LeadingSource", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedLeadingSource: () => {
    set({ addedLeadingSource: null });
  },
  clearUpdatedLeadingSource: () => {
    set({ updatedLeadingSource: null });
  },
  clearDeletedLeadingSource: () => {
    set({ deletedLeadingSource: null });
  },
}));
